import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { store, persistor } from '@/store'
import { queryClient } from '@/api/queryClient'
import { ThemeProvider } from '@/theme/ThemeProvider'
import App from './App.jsx'
import './index.css'
import './theme/fluent3d.css'
import { loadServerSkin } from '@/theme/skin'
import { holdBootLoader, markAppMounted } from '@/boot'
// TEMPORARY: internal-testing feedback + error capture.
// Remove per src/observability/README.md before public launch.
import { initObservability } from '@/observability'
import { installPreloadErrorReload } from '@/utils/lazyWithRetry'

// Deliberately not awaited: boot must not wait on, or depend on, telemetry.
initObservability()

// A tab left open across a deploy asks for chunks that no longer exist;
// reload once onto the new build instead of failing (GRAPHIT-3R, GRAPHIT-4Y).
installPreloadErrorReload()

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          {/* Inside the store (it reads organization settings and auth),
              outside the router (it has nothing to do with routing). */}
          <ThemeProvider>
            <BrowserRouter
              basename={import.meta.env.BASE_URL.replace(/\/$/, '')}
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <App />
            </BrowserRouter>
          </ThemeProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
)

// The interface theme comes from the server; the boot preloader stays up until
// it has answered (or 2.5 s have passed), so no one sees the wrong theme first.
const releaseThemeHold = holdBootLoader()
Promise.race([loadServerSkin(), new Promise((resolve) => setTimeout(resolve, 2500))]).finally(releaseThemeHold)

// The boot preloader fades once this first frame is up and no page-level loader is waiting.
requestAnimationFrame(() => markAppMounted())
